import { ErrorHandler, Injectable, inject } from '@angular/core';
import { type CapabilityDefect, findCapabilityDefects } from './vendor-capability-defects';
import type { ResolvedVendor } from './vendor-channel-resolver.service';
import { VendorRegistryService } from './vendor-registry.service';

const KEY_SEPARATOR = '\u0000';

/**
 * The one place ADR-0006 capability defects reach ErrorHandler. Every ingress ACL hands
 * its decoded batch here with the feed it came from.
 *
 * Reports at most once per (table, kind, vendor, channel) for the service's lifetime. The
 * de-dup is not cosmetic: prod holds online vendor keys with no vendordb record at all on
 * most rows -- `bangerhead` on 48,628, `gaston` on 25,499 (evidence record
 * .scratch/fact-families-persistence/evidence-2026-08-27-distribution-c-key.md, query 9)
 * -- so a per-read report would fire on nearly every distribution and sales read until
 * those rows exist.
 */
@Injectable({ providedIn: 'root' })
export class CapabilityDefectReporter {
  private readonly errorHandler = inject(ErrorHandler);
  private readonly vendorRegistry = inject(VendorRegistryService);
  private readonly reported = new Set<string>();

  /** `table` names the feed the batch was decoded from and is part of the de-dup key. */
  report(table: string, decoded: Iterable<ResolvedVendor>): void {
    // The registry loads asynchronously over HTTP; before it lands every vendor
    // looks unknown, so a read decoded that early is judged against nothing.
    if (this.vendorRegistry.allEntries().length === 0) return;

    this.reportDefects(
      table,
      findCapabilityDefects(decoded, (vendor) => this.vendorRegistry.get(vendor)?.channels)
    );
  }

  /** Reports defects an ACL computed itself; needs no registry, so it is not gated on it loading. */
  reportDefects(table: string, defects: Iterable<CapabilityDefect>): void {
    for (const defect of defects) {
      const key = [table, defect.kind, defect.vendor, defect.channel].join(KEY_SEPARATOR);
      if (this.reported.has(key)) continue;
      this.reported.add(key);

      // ErrorHandler, not the console channel the ACLs use for decode failures: a
      // capability defect is a data-quality signal worth a Sentry event, a decode
      // failure is a local diagnostic.
      this.errorHandler.handleError(
        new Error(`[CapabilityDefectReporter] capability defect in ${table}: ${describe(defect)}`)
      );
    }
  }
}

function describe({ kind, vendor, channel }: CapabilityDefect): string {
  switch (kind) {
    case 'unregistered-vendor':
      return `vendor "${vendor}" has no vendordb record (observed channel "${channel}")`;
    case 'undeclared-channel':
      return `vendor "${vendor}" observed on channel "${channel}", which vendordb does not declare`;
    case 'undeclared-price-convention':
      return `vendor "${vendor}" has priced days on channel "${channel}" but declares no price-field convention`;
    case 'price-convention-contradicted':
      return `vendor "${vendor}" on channel "${channel}" charges above list on too many priced days for its declared price-field convention`;
  }
}
